import { useCallback, useEffect, useMemo, useState } from 'react';
import izenuAPI from '../../services/izenuAPI';

// Publicidade → Receitas: advertising sold by the team (api/admin/ads/campaigns.php).
// Each campaign is split by calendar month and each month becomes an invoice in
// Odoo when the month starts. Audio ads enter the royalty pool (70% to the
// artists); banners are the platform's revenue. Kz and USD only.

const inputCls = 'block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:ring-indigo-500 disabled:bg-gray-100';
const btnPrimary = 'rounded-lg bg-indigo-500 px-4 py-2 text-sm font-medium !text-white hover:bg-indigo-600 disabled:opacity-50';
const btnGhost = 'rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50';
const MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const monthLabel = (ym) => { const [y, m] = ym.split('-').map(Number); return `${MONTHS[m - 1]} ${y}`; };
const money = (v, c) => (c === 'USD'
    ? new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'USD' }).format(Number(v) || 0)
    : `${new Intl.NumberFormat('pt-AO', { maximumFractionDigits: 0 }).format(Number(v) || 0)} Kz`);
const fmtDate = (s) => (s ? new Date(`${String(s).slice(0, 10)}T12:00:00`).toLocaleDateString('pt-PT') : '—');
const today = () => new Date().toISOString().slice(0, 10);
const STATUS = { pending: ['Por pagar', 'bg-amber-50 text-amber-800'], paid: ['Paga', 'bg-green-50 text-green-700'], cancelled: ['Cancelada', 'bg-gray-100 text-gray-500'] };

/** Same split as AdCampaigns::split(): by days, the last month takes the rounding. */
function split(start, end, amount) {
    if (!start || !end || end < start || !(amount > 0)) return [];
    const day = (s) => new Date(`${s}T12:00:00Z`).getTime() / 86400000;
    const total = Math.round(day(end) - day(start)) + 1;
    const out = [];
    let cursor = start;
    while (cursor <= end && out.length < 30) {
        const [y, m] = cursor.split('-').map(Number);
        const monthEnd = new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
        const last = monthEnd < end ? monthEnd : end;
        out.push({ period: cursor.slice(0, 7), days: Math.round(day(last) - day(cursor)) + 1 });
        cursor = new Date(Date.UTC(y, m, 1)).toISOString().slice(0, 10);
    }
    let left = amount;
    out.forEach((o, i) => {
        o.amount = i === out.length - 1 ? Math.round(left * 100) / 100 : Math.round((amount * o.days / total) * 100) / 100;
        left -= o.amount;
    });
    return out;
}

// Ads on the music (audio between songs, banner over the cover) are royalties;
// the other banners are the platform's. Same rule as AdCampaigns::kindOf().
const ROYALTY_KINDS = ['audio', 'artwork'];

function PoolBadge({ kind }) {
    return ROYALTY_KINDS.includes(kind)
        ? <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-700">Royalties · 70% artistas</span>
        : <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600">Receita da plataforma</span>;
}

const EMPTY = {
    advertiser_name: '', advertiser_email: '', advertiser_nif: '', advertiser_phone: '', advertiser_country: 'AO',
    ad_type: 'audio', ad_id: '', amount: '', currency: 'AOA', vat_included: true,
    start_date: today(), end_date: '', paid: false, paid_at: today(), payment_ref: '', notes: '',
};

function NewCampaign({ data, onDone, onClose }) {
    const [f, setF] = useState(EMPTY);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const set = (k, v) => setF((x) => ({ ...x, [k]: v, ...(k === 'ad_type' ? { ad_id: '' } : {}) }));

    const vat = Number(data.vat_pct || 14);
    const gross = f.vat_included ? Number(f.amount) : Number(f.amount) * (100 + vat) / 100;
    const months = useMemo(() => split(f.start_date, f.end_date, gross), [f.start_date, f.end_date, gross]);
    const kind = ROYALTY_KINDS.includes(f.ad_type) ? f.ad_type : 'banner';
    const net = gross / (1 + vat / 100);

    const save = async () => {
        setBusy(true); setError('');
        try {
            await izenuAPI.post('/admin/ads/campaigns.php', { create: { ...f, amount: Number(f.amount) } });
            onDone();
        } catch (e) {
            setError(e?.response?.data?.message || 'Não foi possível guardar.');
        }
        setBusy(false);
    };

    const label = (t, req) => <span className="mb-1 block text-xs font-medium text-gray-600">{t}{req && <span className="text-red-500"> *</span>}</span>;

    return (
        <section className="space-y-5 rounded-xl border border-indigo-200 bg-white p-4 sm:p-6">
            <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Nova campanha</h2>
                <button type="button" onClick={onClose} className="text-xl text-gray-400 hover:text-gray-700">×</button>
            </div>

            <div>
                <h3 className="mb-2 text-sm font-semibold text-gray-800">Anunciante</h3>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    <label>{label('Nome / empresa', true)}<input className={inputCls} value={f.advertiser_name} onChange={(e) => set('advertiser_name', e.target.value)} /></label>
                    <label>{label('Email', true)}<input className={inputCls} type="email" value={f.advertiser_email} onChange={(e) => set('advertiser_email', e.target.value)} placeholder="Identifica o cliente na Odoo" /></label>
                    <label>{label('NIF')}<input className={inputCls} value={f.advertiser_nif} onChange={(e) => set('advertiser_nif', e.target.value)} /></label>
                    <label>{label('Telefone')}<input className={inputCls} value={f.advertiser_phone} onChange={(e) => set('advertiser_phone', e.target.value)} /></label>
                    <label>{label('País')}
                        <select className={inputCls} value={f.advertiser_country} onChange={(e) => set('advertiser_country', e.target.value)}>
                            <option value="AO">Angola</option><option value="PT">Portugal</option><option value="BR">Brasil</option><option value="ZA">África do Sul</option><option value="US">Estados Unidos</option>
                        </select></label>
                </div>
            </div>

            <div>
                <h3 className="mb-2 text-sm font-semibold text-gray-800">Anúncio</h3>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <label>{label('Onde aparece', true)}
                        <select className={inputCls} value={f.ad_type} onChange={(e) => set('ad_type', e.target.value)}>
                            {Object.entries(data.types || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select></label>
                    <label>{label('Anúncio (opcional)')}
                        <select className={inputCls} value={f.ad_id} onChange={(e) => set('ad_id', e.target.value)}>
                            <option value="">— Ainda não carregado / não indicar —</option>
                            {(data.ads?.[f.ad_type] || []).map((a) => <option key={a.id} value={a.id}>{a.title || `#${a.id}`}{a.exp_date ? ` · até ${fmtDate(a.exp_date)}` : ''}</option>)}
                        </select></label>
                </div>
                <p className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                    <PoolBadge kind={kind} />
                    {kind === 'audio' ? 'Toca entre as músicas: entra no bolo dos royalties, como as assinaturas.'
                        : kind === 'artwork' ? 'Aparece sobre a capa da música no leitor: entra no bolo dos royalties, como as assinaturas.'
                        : 'Banner: a receita fica para a Izenu e não entra nos royalties.'}
                </p>
            </div>

            <div>
                <h3 className="mb-2 text-sm font-semibold text-gray-800">Valor e período</h3>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <label>{label('Valor', true)}<input className={inputCls} type="number" min="0" step="0.01" value={f.amount} onChange={(e) => set('amount', e.target.value)} /></label>
                    <label>{label('Moeda', true)}
                        <select className={inputCls} value={f.currency} onChange={(e) => set('currency', e.target.value)}>
                            <option value="AOA">Kz (AOA)</option><option value="USD">USD</option>
                        </select></label>
                    <label>{label('Início', true)}<input className={inputCls} type="date" value={f.start_date} onChange={(e) => set('start_date', e.target.value)} /></label>
                    <label>{label('Fim', true)}<input className={inputCls} type="date" min={f.start_date} value={f.end_date} onChange={(e) => set('end_date', e.target.value)} /></label>
                </div>
                <label className="mt-2 inline-flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" checked={f.vat_included} onChange={(e) => set('vat_included', e.target.checked)} /> O valor já inclui IVA ({vat}%)
                </label>
                {Number(f.amount) > 0 && (
                    <p className="mt-1 text-xs text-gray-500">
                        Faturado: <b>{money(gross, f.currency)}</b> · IVA {money(gross - net, f.currency)} · líquido {money(net, f.currency)}
                        {ROYALTY_KINDS.includes(kind) && <> · para os artistas (70%) cerca de <b>{money(net * 0.7, f.currency)}</b></>}
                    </p>
                )}
                {months.length > 0 && (
                    <div className="mt-3 overflow-x-auto">
                        <table className="text-xs">
                            <thead><tr className="text-left text-gray-500">{months.map((m) => <th key={m.period} className="pr-4 font-semibold capitalize">{monthLabel(m.period)}</th>)}</tr></thead>
                            <tbody><tr className="text-gray-800">{months.map((m) => <td key={m.period} className="pr-4 tabular-nums">{money(m.amount, f.currency)} <span className="text-gray-400">({m.days} d)</span></td>)}</tr></tbody>
                        </table>
                        <p className="mt-1 text-xs text-gray-500">Uma fatura por mês na Odoo, criada quando o mês começa.</p>
                    </div>
                )}
            </div>

            <div>
                <label className="inline-flex items-center gap-2 text-sm font-semibold text-gray-800">
                    <input type="checkbox" checked={f.paid} onChange={(e) => set('paid', e.target.checked)} /> O anunciante já pagou
                </label>
                {f.paid && (
                    <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-3">
                        <label>{label('Data do pagamento')}<input className={inputCls} type="date" max={today()} value={f.paid_at} onChange={(e) => set('paid_at', e.target.value)} /></label>
                        <label className="sm:col-span-2">{label('Referência / comprovativo')}<input className={inputCls} value={f.payment_ref} onChange={(e) => set('payment_ref', e.target.value)} /></label>
                    </div>
                )}
                {!f.paid && <p className="mt-1 text-xs text-gray-500">As faturas ficam por pagar na Odoo. Marca a campanha como paga quando o dinheiro entrar — só faturas pagas contam para os royalties.</p>}
            </div>

            <label className="block">{label('Notas internas')}<input className={inputCls} value={f.notes} onChange={(e) => set('notes', e.target.value)} /></label>

            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex justify-end gap-2">
                <button type="button" className={btnGhost} onClick={onClose}>Cancelar</button>
                <button type="button" className={btnPrimary} disabled={busy || !f.advertiser_name || !f.advertiser_email || !(Number(f.amount) > 0) || !f.end_date} onClick={save}>
                    {busy ? 'A guardar…' : 'Guardar e faturar'}
                </button>
            </div>
        </section>
    );
}

function CampaignRow({ c, onChanged }) {
    const [open, setOpen] = useState(false);
    const [paying, setPaying] = useState(false);
    const [pay, setPay] = useState({ paid_at: today(), payment_ref: '' });
    const [busy, setBusy] = useState(false);
    const errors = c.months.filter((m) => m.last_error);

    const post = async (body) => {
        setBusy(true);
        try {
            await izenuAPI.post('/admin/ads/campaigns.php', body);
            setPaying(false);
            onChanged();
        } catch (e) {
            window.alert(e?.response?.data?.message || 'Não foi possível concluir.');
        }
        setBusy(false);
    };

    return (
        <>
            <tr className="align-top">
                <td className="py-3 pr-3">
                    <button type="button" onClick={() => setOpen((v) => !v)} className="text-left">
                        <div className="font-medium text-gray-900">{c.advertiser_name}</div>
                        <div className="text-xs text-gray-500">#{c.id} · {c.advertiser_email}</div>
                    </button>
                </td>
                <td className="py-3 pr-3 text-sm text-gray-700">
                    {c.ad_title || '—'}
                    <div className="mt-1"><PoolBadge kind={c.kind} /></div>
                </td>
                <td className="whitespace-nowrap py-3 pr-3 text-sm text-gray-600">{fmtDate(c.start_date)} – {fmtDate(c.end_date)}</td>
                <td className="whitespace-nowrap py-3 pr-3 text-right font-semibold tabular-nums text-gray-900">{money(c.amount, c.currency)}</td>
                <td className="py-3 pr-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS[c.status][1]}`}>{STATUS[c.status][0]}</span>
                    {c.status === 'paid' && <div className="mt-1 text-xs text-gray-500">{fmtDate(c.paid_at)}{c.payment_ref ? ` · ${c.payment_ref}` : ''}</div>}
                    {errors.length > 0 && <div className="mt-1 text-xs font-semibold text-red-600">Erro na Odoo ({errors.length})</div>}
                </td>
                <td className="whitespace-nowrap py-3 text-right">
                    {c.status === 'pending' && !paying && (
                        <div className="flex justify-end gap-2">
                            <button type="button" className={btnPrimary} onClick={() => setPaying(true)}>Marcar como paga</button>
                            <button type="button" className={btnGhost} disabled={busy} onClick={() => window.confirm('Cancelar a campanha? As faturas por pagar são anuladas na Odoo.') && post({ cancel: c.id })}>Cancelar</button>
                        </div>
                    )}
                    {paying && (
                        <div className="ml-auto grid w-60 gap-2 text-left">
                            <input className={inputCls} type="date" max={today()} value={pay.paid_at} onChange={(e) => setPay({ ...pay, paid_at: e.target.value })} />
                            <input className={inputCls} placeholder="Referência / comprovativo" value={pay.payment_ref} onChange={(e) => setPay({ ...pay, payment_ref: e.target.value })} />
                            <div className="flex gap-2">
                                <button type="button" className={btnPrimary} disabled={busy} onClick={() => post({ pay: c.id, ...pay })}>{busy ? 'A registar…' : 'Confirmar'}</button>
                                <button type="button" className={btnGhost} onClick={() => setPaying(false)}>Voltar</button>
                            </div>
                        </div>
                    )}
                    <button type="button" onClick={() => setOpen((v) => !v)} className="mt-2 block w-full text-right text-xs text-indigo-600 hover:underline">{open ? 'Esconder meses' : `Ver ${c.months.length} meses`}</button>
                </td>
            </tr>
            {open && (
                <tr>
                    <td colSpan={6} className="pb-4">
                        <div className="rounded-lg bg-gray-50 p-3">
                            <table className="w-full text-xs">
                                <thead><tr className="text-left text-gray-500"><th className="pb-1 pr-3 font-semibold">Mês</th><th className="pb-1 pr-3 font-semibold">Dias</th><th className="pb-1 pr-3 text-right font-semibold">Valor</th><th className="pb-1 pr-3 font-semibold">Fatura na Odoo</th><th className="pb-1 font-semibold">Estado</th></tr></thead>
                                <tbody>
                                    {c.months.map((m) => (
                                        <tr key={m.period} className="border-t border-gray-200">
                                            <td className="py-1.5 pr-3 capitalize text-gray-900">{monthLabel(m.period)}</td>
                                            <td className="py-1.5 pr-3 text-gray-600">{m.days}</td>
                                            <td className="py-1.5 pr-3 text-right tabular-nums text-gray-900">{money(m.amount, c.currency)}</td>
                                            <td className="py-1.5 pr-3 font-mono text-gray-700">{m.odoo_invoice_name || <span className="font-sans text-gray-400">Criada quando o mês começar</span>}</td>
                                            <td className="py-1.5">
                                                {Number(m.odoo_cancelled) ? <span className="text-gray-500">Anulada</span>
                                                    : Number(m.odoo_paid) ? <span className="text-green-700">Paga</span>
                                                        : m.odoo_invoice_id ? <span className="text-amber-700">Por pagar</span> : <span className="text-gray-400">—</span>}
                                                {m.last_error && <div className="text-red-600">{m.last_error}</div>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {c.notes && <p className="mt-2 text-xs text-gray-600">Notas: {c.notes}</p>}
                            {errors.length > 0 && <button type="button" className={`${btnGhost} mt-2`} disabled={busy} onClick={() => post({ sync: 1 })}>Tentar de novo na Odoo</button>}
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}

export default function AdRevenue() {
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [creating, setCreating] = useState(false);
    const [filter, setFilter] = useState('');

    const load = useCallback(() => {
        izenuAPI.get('/admin/ads/campaigns.php')
            .then(({ data: d }) => setData(d))
            .catch(() => setError('Não foi possível carregar as campanhas.'));
    }, []);
    useEffect(() => { load(); }, [load]);

    if (!data) return <p className="mx-auto max-w-6xl p-6 text-sm text-gray-500">{error || 'A carregar…'}</p>;

    const rows = (data.data || []).filter((c) => !filter || c.status === filter || c.kind === filter
        || (filter === 'royalties' && ROYALTY_KINDS.includes(c.kind)));
    const totals = {};
    (data.data || []).filter((c) => c.status !== 'cancelled').forEach((c) => {
        const k = `${c.status}|${c.currency}`;
        totals[k] = (totals[k] || 0) + Number(c.amount);
    });

    return (
        <div className="mx-auto max-w-6xl space-y-6 p-3 text-left sm:p-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">Receitas de publicidade</h1>
                    <p className="max-w-3xl text-sm text-gray-500">
                        Campanhas pagas por anunciantes, faturadas na Odoo (Kz ou USD, valores com IVA). O <b>áudio entre músicas</b> entra no bolo dos
                        royalties do mês (70% para os artistas); os <b>banners</b> são receita da plataforma. Cada mês da campanha é uma fatura, e só conta
                        para os royalties se estiver paga quando o mês for fechado.
                    </p>
                </div>
                {!creating && <button type="button" className={btnPrimary} onClick={() => setCreating(true)}>Nova campanha</button>}
            </div>

            {data.installed === false && (
                <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">Correr <code>php bin/migration_ad_campaigns.php</code> no servidor.</p>
            )}

            {creating && <NewCampaign data={data} onClose={() => setCreating(false)} onDone={() => { setCreating(false); load(); }} />}

            <div className="flex flex-wrap gap-3">
                {Object.keys(totals).length === 0 && <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-500">Ainda não há campanhas.</div>}
                {Object.entries(totals).map(([k, v]) => {
                    const [status, cur] = k.split('|');
                    return (
                        <div key={k} className="min-w-[180px] rounded-xl border border-gray-200 bg-white px-4 py-3">
                            <div className="text-xs text-gray-500">{status === 'paid' ? 'Recebido' : 'Por receber'} ({cur})</div>
                            <div className="text-xl font-bold tabular-nums text-gray-900">{money(v, cur)}</div>
                        </div>
                    );
                })}
            </div>

            <section className="rounded-xl border border-gray-200 bg-white p-4 sm:p-6">
                <div className="mb-3 flex flex-wrap items-center gap-2">
                    <span className="text-sm text-gray-600">Mostrar:</span>
                    {[['', 'Todas'], ['pending', 'Por pagar'], ['paid', 'Pagas'], ['royalties', 'Royalties (áudio e capa)'], ['banner', 'Outros banners'], ['cancelled', 'Canceladas']].map(([k, t]) => (
                        <button key={k} type="button" onClick={() => setFilter(k)} className={`rounded-full px-3 py-1 text-xs font-semibold ${filter === k ? 'bg-indigo-500 !text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>{t}</button>
                    ))}
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[820px] text-sm">
                        <thead><tr className="text-left text-xs uppercase text-gray-500">
                            <th className="pb-2 pr-3 font-semibold">Anunciante</th><th className="pb-2 pr-3 font-semibold">Anúncio</th>
                            <th className="pb-2 pr-3 font-semibold">Período</th><th className="pb-2 pr-3 text-right font-semibold">Valor (c/ IVA)</th>
                            <th className="pb-2 pr-3 font-semibold">Estado</th><th className="pb-2" />
                        </tr></thead>
                        <tbody className="divide-y divide-gray-100">
                            {rows.length === 0 && <tr><td colSpan={6} className="py-4 text-sm text-gray-500">Nenhuma campanha.</td></tr>}
                            {rows.map((c) => <CampaignRow key={c.id} c={c} onChanged={load} />)}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
}
